import { Controller } from "@hotwired/stimulus"

// PaletteLivePreviewController — handles every cross-template live-
// preview pattern in one place. Mount alongside a per-template
// controller via `data-controller="palette-live-preview <template>-live-preview"`:
// both fire independently on every change event, each acts only on the
// fields it owns (matched via `data-live-preview-field`).
//
// What this controller owns:
//   * Palette swap on style_preset change (CSS variables + bg layer
//     repaints + category-bar tinting via [data-category-color-index]).
//   * Background asset swap on bg_asset radio change.
//   * Background filter live updates (overlay_darkness / tint /
//     tint_opacity / blur_px / saturate).
//   * Kit-wide text_color + text_shadow application — opts elements in
//     via `data-text-color-target` / `data-text-shadow-target`. Per-
//     template controllers that own their own colour/shadow logic
//     (Book Takeaway, Daily Quote) keep working in parallel because
//     both controllers fire on every event; this controller is a no-op
//     when no opted-in elements exist.
//
// What stays per-template:
//   * Chart-type / direction / reveal-order / chart-content updates
//     specific to a single composition. Templates implement these in
//     their own controller, listening on `data-live-preview-field` too.
//
// CONTRACT: every input that should drive a live update MUST carry
// `data-live-preview-field="<field>"`, and every preset radio MUST
// carry `data-palette-json` with the full palette payload. Picker
// components emit both already.
export default class PaletteLivePreviewController extends Controller {
  static targets = ["frame", "backgroundImage", "backgroundTint", "backgroundDarkness"]

  connect() {
    this.boundChange = this.handleChange.bind(this)
    this.inputs().forEach((el) => el.addEventListener("change", this.boundChange))
    this.inputs("input").forEach((el) => el.addEventListener("input", this.boundChange))
  }

  disconnect() {
    this.inputs().forEach((el) => el.removeEventListener("change", this.boundChange))
    this.inputs("input").forEach((el) => el.removeEventListener("input", this.boundChange))
  }

  inputs(eventName = "change") {
    // Sliders need the "input" event for live drag updates; everything
    // else is fine with "change". Two listeners keep both flows working
    // without double-firing on `change` for sliders (since sliders
    // already get caught by "input" before "change" finalises).
    if (eventName === "input") {
      return this.element.querySelectorAll(
        '[data-live-preview-field][type="range"], [data-live-preview-field][type="color"]',
      )
    }
    return this.element.querySelectorAll('[data-live-preview-field]')
  }

  handleChange(event) {
    const target = event.target
    const field = target.dataset.livePreviewField
    if (!field) return
    if (target.type === "radio" && !target.checked) return

    switch (field) {
      case "style_preset":
        this.applyPreset(target)
        break
      case "bg_asset":
        this.applyBackgroundAsset(target)
        break
      case "overlay_darkness":
      case "tint":
      case "tint_opacity":
      case "blur_px":
      case "saturate":
        this.applyBackgroundFilter()
        break
      case "text_color":
        this.applyTextColor(target.value)
        break
      case "text_shadow":
        this.applyTextShadow(target.checked)
        break
    }
  }

  // Apply colour to every element opted in via `data-text-color-target`.
  // Templates that own their own per-element colour logic don't mark
  // elements with this attr — for them, this is a no-op and their
  // template controller handles the field.
  applyTextColor(color) {
    if (!color) return
    const targets = this.element.querySelectorAll("[data-text-color-target]")
    targets.forEach((el) => { el.style.color = color })
  }

  // Toggle drop shadow on every element opted in via
  // `data-text-shadow-target`. Same opt-in-or-no-op logic as
  // applyTextColor — co-existence with per-template controllers.
  applyTextShadow(enabled) {
    const targets = this.element.querySelectorAll("[data-text-shadow-target]")
    targets.forEach((el) => {
      el.style.textShadow = enabled
        ? "0 2px 12px rgba(0,0,0,0.6)"
        : "none"
    })
  }

  // Palette swap. Reads the JSON blob the picker baked into the radio's
  // data-palette-json attribute — no per-niche palette import needed,
  // the picker is the single source of truth at render time.
  applyPreset(radio) {
    if (!this.hasFrameTarget) return
    if (!radio.dataset.paletteJson) return

    let palette
    try {
      palette = JSON.parse(radio.dataset.paletteJson)
    } catch (e) {
      return
    }

    const frame = this.frameTarget
    frame.style.setProperty("--pp-bg", palette.background)
    frame.style.setProperty("--pp-surface", palette.surface)
    frame.style.setProperty("--pp-text", palette.text)
    frame.style.setProperty("--pp-muted", palette.textMuted)
    frame.style.setProperty("--pp-accent", palette.accent)
    frame.style.setProperty("--pp-positive", palette.positive)
    frame.style.setProperty("--pp-negative", palette.negative)
    frame.style.setProperty("--pp-neutral", palette.neutral)

    this.recolorCategoryBars(palette.categoryColors)
    this.afterPresetChange?.(palette)
  }

  // Auto-tint any element marked as a category-coloured bar/swatch
  // (e.g. budget breakdown legend swatches). Templates wanting more
  // than this hook can override `afterPresetChange(palette)`.
  recolorCategoryBars(categoryColors) {
    if (!categoryColors || categoryColors.length === 0) return
    const bars = this.element.querySelectorAll("[data-category-color-index]")
    bars.forEach((el) => {
      const i = parseInt(el.dataset.categoryColorIndex, 10) || 0
      const color = categoryColors[i % categoryColors.length]
      el.style.background = color
      el.style.color = color
    })
  }

  // Asset radio button — data-bg-preview-url on the radio carries the
  // still-snapshot URL. Empty string clears the preview (gradient mode).
  // Frame target is optional: previews built without Common::PreviewFrameComponent
  // (Book Takeaway, Quiz) only have backgroundImage; previews built with it
  // (Number Reveal et al.) have both.
  applyBackgroundAsset(radio) {
    const url = radio.dataset.bgPreviewUrl || ""
    if (this.hasFrameTarget) this.frameTarget.dataset.bgSrc = url
    if (this.hasBackgroundImageTarget) {
      this.backgroundImageTarget.style.backgroundImage = url ? `url('${url}')` : ""
    }
  }

  // Reads all four filter fields and repaints the tint/darkness layers
  // + bg-image CSS filter. Mirrors BackgroundRenderer's tint-then-
  // darkness layering (both can be active at once).
  applyBackgroundFilter() {
    const read = (field) =>
      this.element.querySelector(`[data-live-preview-field="${field}"]`)?.value || ""

    const darkness = parseFloat(read("overlay_darkness")) || 0
    const tint = read("tint").trim()
    const tintOpacity = parseFloat(read("tint_opacity")) || 0
    const blur = parseFloat(read("blur_px")) || 0
    const saturate = parseFloat(read("saturate"))

    if (this.hasBackgroundTintTarget) {
      this.backgroundTintTarget.style.background =
        tint && tintOpacity > 0
          ? `color-mix(in oklab, ${tint} ${Math.round(tintOpacity * 100)}%, transparent)`
          : ""
    }
    if (this.hasBackgroundDarknessTarget) {
      this.backgroundDarknessTarget.style.background =
        darkness > 0 ? `rgba(0, 0, 0, ${darkness})` : ""
    }
    if (this.hasBackgroundImageTarget) {
      const parts = []
      if (blur > 0) parts.push(`blur(${blur}px)`)
      if (!isNaN(saturate) && saturate !== 1) parts.push(`saturate(${saturate})`)
      this.backgroundImageTarget.style.filter = parts.join(" ")
    }
  }
}
